import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Printer, Send, Trash2, X } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import { useContractorPayablesAccess } from '../hooks/useContractorPayablesAccess';
import { CONTRACTOR_1099_NEC_THRESHOLD } from '../utils/contractorPayablesAccess';
import {
  formatCallableError,
  formatWeekRange,
  getContractorProfile,
  groupPendingDraftsByWeek,
  listPaidInvoicesYtd,
  deleteContractorInvoice,
  markContractorInvoicePaid,
  submitPendingContractorWeek,
  subscribeAllContractorInvoices,
  subscribeAllPendingDraftEntries,
  type PendingContractorWeekGroup,
} from '../utils/contractorTimesheetService';
import {
  defaultSubmitBillingFromProfile,
  totalBillableDays,
} from '../utils/contractorInvoiceBilling';
import type { ContractorInvoice, ContractorProfile, ContractorTimeEntry } from '../types';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { firestoreSnapshotListenError } from '../utils/firestoreListenDebug';
import { Navigate } from 'react-router-dom';
import '../components/pm/pm-shared.css';
import './ContractorPayablesPage.css';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatHours(minutes: number): string {
  return `${(minutes / 60).toFixed(2)}h`;
}

function invoiceJobLabel(line: ContractorInvoice['lineItems'][number]): string {
  const num = line.projectNumber?.trim();
  const numPart = num ? `#${num.replace(/^#+/, '')}` : '';
  const name = (line.projectName || '').trim();
  const qty =
    line.billingUnit === 'day' && typeof line.days === 'number'
      ? ` · ${line.days} day${line.days === 1 ? '' : 's'}`
      : '';
  if (numPart && name) return `${numPart} — ${name}${qty}`;
  return (name || numPart || line.projectId || 'Project') + qty;
}

function lineQuantityLabel(line: ContractorInvoice['lineItems'][number]): string {
  if (line.billingUnit === 'day' && typeof line.days === 'number') {
    return `${line.days} day${line.days === 1 ? '' : 's'} (${formatHours(line.minutes)} worked)`;
  }
  return formatHours(line.minutes);
}

function lineRateLabel(line: ContractorInvoice['lineItems'][number]): string {
  if (line.billingUnit === 'day') return `${formatCurrency(line.rate)}/day`;
  return `${formatCurrency(line.rate)}/hr`;
}

interface ContractorPayablesPageProps {
  /** When true, omit AppHeader (rendered inside FinancesHub). */
  embedded?: boolean;
  /** Hide submit / mark-paid (CPA portal and admin preview). */
  readOnly?: boolean;
}

const ContractorPayablesPage: React.FC<ContractorPayablesPageProps> = ({ embedded, readOnly = false }) => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canAccess, canManage } = useContractorPayablesAccess();
  const canWritePayables = !readOnly && canManage;

  const [invoices, setInvoices] = useState<ContractorInvoice[]>([]);
  const [draftEntries, setDraftEntries] = useState<ContractorTimeEntry[]>([]);
  const [ytdPaid, setYtdPaid] = useState<ContractorInvoice[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(searchParams.get('invoice'));
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending_payment' | 'paid'>('pending_payment');
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [qbNote, setQbNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [submittingWeekKey, setSubmittingWeekKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [listenError, setListenError] = useState<string | null>(null);
  const [contractorProfiles, setContractorProfiles] = useState<Map<string, ContractorProfile | null>>(new Map());
  const [pendingSubmitRates, setPendingSubmitRates] = useState<Record<string, string>>({});

  const year = new Date().getFullYear();

  useEffect(() => {
    const unsub = subscribeAllContractorInvoices(
      (rows) => {
        setInvoices(rows);
        setListenError(null);
      },
      (err) => {
        firestoreSnapshotListenError('ContractorPayablesPage', 'contractorInvoices', err, {
          userId: user?.id,
          role: user?.role,
          email: user?.email,
        });
        const code = err && typeof err === 'object' && 'code' in err ? String((err as { code?: string }).code) : '';
        if (code === 'permission-denied') {
          setListenError(
            'Cannot load invoices — your account may be missing Executive Assistant designation in User Management (Firestore profile).',
          );
        } else {
          setListenError('Cannot load contractor invoices. Try refreshing the page.');
        }
      },
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  useEffect(() => {
    const unsub = subscribeAllPendingDraftEntries(
      setDraftEntries,
      (err) => {
        firestoreSnapshotListenError('ContractorPayablesPage', 'contractorTimeEntries(draft)', err, {
          userId: user?.id,
          role: user?.role,
          email: user?.email,
        });
      },
    );
    return unsub;
  }, [user?.id, user?.role, user?.email]);

  useEffect(() => {
    listPaidInvoicesYtd(year).then(setYtdPaid).catch(() => setYtdPaid([]));
  }, [year, invoices]);

  useEffect(() => {
    const id = searchParams.get('invoice');
    if (id) setSelectedId(id);
  }, [searchParams]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return invoices;
    return invoices.filter((i) => i.status === statusFilter);
  }, [invoices, statusFilter]);

  const selected = useMemo(
    () => invoices.find((i) => i.id === selectedId) || null,
    [invoices, selectedId],
  );

  const ytdByContractor = useMemo(() => {
    const map = new Map<string, { name: string; total: number }>();
    for (const inv of ytdPaid) {
      const row = map.get(inv.contractorUserId) || { name: inv.contractorName, total: 0 };
      row.total += inv.totalAmount;
      map.set(inv.contractorUserId, row);
    }
    return Array.from(map.entries())
      .map(([userId, row]) => ({ userId, ...row }))
      .sort((a, b) => b.total - a.total);
  }, [ytdPaid]);

  const pendingWeeks = useMemo(() => groupPendingDraftsByWeek(draftEntries), [draftEntries]);

  useEffect(() => {
    const ids = Array.from(new Set(pendingWeeks.map((g) => g.contractorUserId)));
    if (ids.length === 0) {
      setContractorProfiles(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const pairs = await Promise.all(
        ids.map(async (id) => ({ id, profile: await getContractorProfile(id) })),
      );
      if (cancelled) return;
      setContractorProfiles(new Map(pairs.map((p) => [p.id, p.profile])));
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingWeeks]);

  const pendingWeekEntries = useCallback(
    (group: PendingContractorWeekGroup) =>
      draftEntries.filter(
        (e) =>
          e.contractorUserId === group.contractorUserId
          && e.workDate.getTime() >= group.weekStart.getTime()
          && e.workDate.getTime() <= group.weekEnd.getTime(),
      ),
    [draftEntries],
  );

  const pendingRateForWeek = useCallback(
    (group: PendingContractorWeekGroup, key: string) => {
      const stored = pendingSubmitRates[key];
      if (stored !== undefined && stored !== '') return stored;
      const profile = contractorProfiles.get(group.contractorUserId);
      const { rate } = defaultSubmitBillingFromProfile(profile);
      return rate > 0 ? String(rate) : '';
    },
    [contractorProfiles, pendingSubmitRates],
  );

  const contractorName = (userId: string) =>
    crmUsers.find((u) => u.uid === userId)?.displayName || userId;

  const weekKey = (group: PendingContractorWeekGroup) =>
    `${group.contractorUserId}|${group.weekStart.getTime()}`;

  const handleSubmitPendingWeek = async (group: PendingContractorWeekGroup) => {
    const key = weekKey(group);
    const profile = contractorProfiles.get(group.contractorUserId);
    const billingDefaults = defaultSubmitBillingFromProfile(profile);
    const parsedRate = parseFloat(pendingRateForWeek(group, key));
    if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
      setError(
        billingDefaults.billingUnit === 'day'
          ? 'Enter a valid day rate before submitting.'
          : 'Enter a valid hourly rate before submitting.',
      );
      return;
    }
    setSubmittingWeekKey(key);
    setError(null);
    try {
      const result = await submitPendingContractorWeek(group, {
        billingUnit: billingDefaults.billingUnit,
        rate: parsedRate,
      });
      setSelectedId(result.invoiceId);
      setSearchParams({ invoice: result.invoiceId });
      setStatusFilter('pending_payment');
    } catch (err) {
      setError(formatCallableError(err));
    } finally {
      setSubmittingWeekKey(null);
    }
  };

  const selectInvoice = (id: string) => {
    setSelectedId(id);
    setSearchParams({ invoice: id });
  };

  const handleMarkPaid = async () => {
    if (!selected?.id) return;
    setBusy(true);
    setError(null);
    try {
      await markContractorInvoicePaid(selected.id, qbNote.trim() || undefined);
      setMarkPaidOpen(false);
      setQbNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark paid');
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDeleteInvoice = async () => {
    if (!selected?.id) return;
    setBusy(true);
    setError(null);
    try {
      await deleteContractorInvoice(selected.id);
      setDeleteOpen(false);
      setSelectedId(null);
      setSearchParams({});
      setStatusFilter('pending_payment');
    } catch (err) {
      setError(formatCallableError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!user || !canAccess) {
    return <Navigate to="/" replace />;
  }

  const body = (
    <div className={`cp-shell ${embedded ? 'cp-embedded' : ''}`}>
      <header className="cp-header">
        <div>
          <h1>Contractor Payables</h1>
          <p className="cp-subtitle">Review contractor invoices, pay in QuickBooks, then mark paid here.</p>
        </div>
        <div className="cp-filters">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            <option value="pending_payment">Pending payment</option>
            <option value="paid">Paid</option>
            <option value="all">All</option>
          </select>
        </div>
      </header>

      {listenError && <div className="cp-error cp-listen-error">{listenError}</div>}

      {pendingWeeks.length > 0 && (
        <section className="cp-pending-panel">
          <h3>Pending hours (not yet invoiced)</h3>
          <p className="cp-subtitle">
            Hours saved on contractor timesheets appear here until submitted. Use Submit to create the invoice.
          </p>
          <table className="cp-table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>Week</th>
                <th>Hours</th>
                <th>Days</th>
                <th>Rate</th>
                <th>Entries</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pendingWeeks.map((group) => {
                const key = weekKey(group);
                const submitting = submittingWeekKey === key;
                const profile = contractorProfiles.get(group.contractorUserId);
                const billingDefaults = defaultSubmitBillingFromProfile(profile);
                const billableDays = totalBillableDays(pendingWeekEntries(group));
                const rateValue = pendingRateForWeek(group, key);
                return (
                  <tr key={key}>
                    <td>{contractorName(group.contractorUserId)}</td>
                    <td>{formatWeekRange(group.weekStart)}</td>
                    <td>{formatHours(group.totalMinutes)}</td>
                    <td>{billingDefaults.billingUnit === 'day' ? billableDays : '—'}</td>
                    <td>
                      <input
                        type="number"
                        min={0.01}
                        step={0.01}
                        className="cp-pending-rate"
                        value={rateValue}
                        disabled={!canWritePayables || !!submittingWeekKey}
                        onChange={(e) =>
                          setPendingSubmitRates((prev) => ({ ...prev, [key]: e.target.value }))
                        }
                        aria-label={
                          billingDefaults.billingUnit === 'day' ? 'Day rate' : 'Hourly rate'
                        }
                      />
                      <span className="cp-pending-rate-unit">
                        {billingDefaults.billingUnit === 'day' ? '/day' : '/hr'}
                      </span>
                    </td>
                    <td>{group.entryCount}</td>
                    <td>
                      {canWritePayables ? (
                      <button
                        type="button"
                        className="cp-btn-primary cp-btn-compact"
                        disabled={!!submittingWeekKey}
                        onClick={() => handleSubmitPendingWeek(group)}
                      >
                        <Send size={14} />
                        {submitting ? 'Submitting…' : 'Submit for payment'}
                      </button>
                      ) : (
                        <span className="cp-subtitle">View only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      )}

      <div className="cp-layout">
        <div className="cp-list-panel">
          <table className="cp-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Contractor</th>
                <th>Period</th>
                <th>Jobs</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="cp-empty">
                    {pendingWeeks.length > 0
                      ? 'No invoices yet — submit pending hours above to create one.'
                      : 'No invoices in this view. Log hours on Contractor Timesheets, then submit for payment.'}
                  </td>
                </tr>
              ) : (
                filtered.map((inv) => (
                  <tr
                    key={inv.id}
                    className={inv.id === selectedId ? 'cp-row-selected' : ''}
                    onClick={() => inv.id && selectInvoice(inv.id)}
                  >
                    <td>{inv.invoiceNumber}</td>
                    <td>{inv.contractorName}</td>
                    <td>{formatWeekRange(inv.periodStart)}</td>
                    <td className="cp-jobs">
                      {inv.lineItems.length === 0 ? (
                        '—'
                      ) : (
                        <ul className="cp-jobs-list">
                          {inv.lineItems.map((line) => (
                            <li key={line.projectId}>{invoiceJobLabel(line)}</li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td>{formatCurrency(inv.totalAmount)}</td>
                    <td>
                      <span className={`cp-status cp-status-${inv.status}`}>{inv.status.replace('_', ' ')}</span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="cp-detail-panel">
          {!selected ? (
            <p className="cp-empty">Select an invoice to review line items.</p>
          ) : (
            <div className="cp-invoice-print" id="contractor-invoice-print">
              <div className="cp-invoice-head">
                <div>
                  <h2>Contractor Invoice</h2>
                  <p className="cp-invoice-num">{selected.invoiceNumber}</p>
                </div>
                <div className="cp-actions">
                  <button type="button" className="cp-btn-secondary" onClick={handlePrint}>
                    <Printer size={15} />
                    Print
                  </button>
                  {canWritePayables && selected.status === 'pending_payment' && (
                    <>
                      <button type="button" className="cp-btn-primary" onClick={() => setMarkPaidOpen(true)}>
                        <CheckCircle2 size={15} />
                        Mark paid
                      </button>
                      <button
                        type="button"
                        className="cp-btn-danger"
                        onClick={() => setDeleteOpen(true)}
                      >
                        <Trash2 size={15} />
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              <p className="cp-invoice-meta">
                <strong>{selected.contractorName}</strong>
                <br />
                Period: {formatWeekRange(selected.periodStart)}
              </p>

              <table className="cp-line-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Qty</th>
                    <th>Rate</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.lineItems.map((line) => (
                    <tr key={line.projectId}>
                      <td>
                        {line.projectNumber ? `#${line.projectNumber.replace(/^#+/, '')} — ` : ''}
                        {line.projectName}
                      </td>
                      <td>{lineQuantityLabel(line)}</td>
                      <td>{lineRateLabel(line)}</td>
                      <td>{formatCurrency(line.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>
                      <strong>Total</strong>
                    </td>
                    <td>
                      <strong>{formatCurrency(selected.totalAmount)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>

              {selected.status === 'paid' && selected.paidAt && (
                <p className="cp-paid-meta">
                  Paid {selected.paidAt.toLocaleDateString()}
                  {selected.qbReferenceNote ? ` · QB ref: ${selected.qbReferenceNote}` : ''}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <section className="cp-ytd-panel">
        <h3>{year} YTD paid totals (1099 prep)</h3>
        {ytdByContractor.length === 0 ? (
          <p className="cp-empty">No paid contractor invoices this year yet.</p>
        ) : (
          <table className="cp-ytd-table">
            <thead>
              <tr>
                <th>Contractor</th>
                <th>YTD paid</th>
                <th>1099-NEC</th>
              </tr>
            </thead>
            <tbody>
              {ytdByContractor.map((row) => (
                <tr key={row.userId}>
                  <td>{row.name}</td>
                  <td>{formatCurrency(row.total)}</td>
                  <td>
                    {row.total >= CONTRACTOR_1099_NEC_THRESHOLD ? (
                      <span className="cp-threshold-flag">≥ ${CONTRACTOR_1099_NEC_THRESHOLD}</span>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {error && <div className="cp-error">{error}</div>}
    </div>
  );

  return (
    <div className="cp-page">
      {!embedded && <AppHeader />}
      {body}

      {deleteOpen && selected && (
        <div className="cp-modal-overlay" onClick={() => setDeleteOpen(false)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-modal-head">
              <h3>Delete invoice</h3>
              <button type="button" className="cp-modal-close" onClick={() => setDeleteOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <p>
              Delete <strong>{selected.invoiceNumber}</strong> ({formatCurrency(selected.totalAmount)}
              )? Hours on this invoice return to <strong>draft</strong> on the contractor&apos;s timesheet so
              you can fix and resubmit. This does not remove a bill already entered in QuickBooks — void that
              separately if needed.
            </p>
            <div className="cp-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={() => setDeleteOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="cp-btn-danger"
                onClick={handleDeleteInvoice}
                disabled={busy}
              >
                {busy ? 'Deleting…' : 'Delete invoice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {markPaidOpen && selected && (
        <div className="cp-modal-overlay" onClick={() => setMarkPaidOpen(false)}>
          <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cp-modal-head">
              <h3>Mark invoice paid</h3>
              <button type="button" className="cp-modal-close" onClick={() => setMarkPaidOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <p>
              Confirm payment for <strong>{selected.invoiceNumber}</strong> ({formatCurrency(selected.totalAmount)}
              ) after entering the bill in QuickBooks.
            </p>
            <label className="cp-field">
              QuickBooks reference (optional)
              <input
                type="text"
                value={qbNote}
                onChange={(e) => setQbNote(e.target.value)}
                placeholder="Check #, bill #, memo…"
              />
            </label>
            <div className="cp-modal-actions">
              <button type="button" className="cp-btn-secondary" onClick={() => setMarkPaidOpen(false)}>
                Cancel
              </button>
              <button type="button" className="cp-btn-primary cp-mark-paid" onClick={handleMarkPaid} disabled={busy}>
                {busy ? 'Saving…' : 'Mark paid'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractorPayablesPage;
